// settings.ts — player-facing render/audio/accessibility settings, persisted to localStorage.
//
// These are PRESENTATION settings only (they never touch Core / the sim / determinism). They are a
// single source of truth shared by the AudioBus (volume/mute) and the juice helpers (reduced-motion,
// haptics). Reads are tolerant of private-mode / blocked storage (best-effort, defaulted).
//
// Reduced-motion default also honours the OS-level `prefers-reduced-motion` media query the first
// time we run, so accessibility users get a calm experience without opening a menu.

const KEY = 'trivia.settings.v1';

export interface RenderSettings {
  /** master volume 0..1 */
  volume: number;
  /** hard mute (independent of volume so a player can mute without losing their level) */
  muted: boolean;
  /** music-bus multiplier 0..1 (applied on top of master) — M3 split mixer */
  musicVolume: number;
  /** SFX+VO-bus multiplier 0..1 (applied on top of master) — M3 split mixer */
  sfxVolume: number;
  /** kill screenshake / heavy particles / hitstop-stacking — calmer, accessible play */
  reducedMotion: boolean;
  /** allow navigator.vibrate haptics on supported mobile devices */
  haptics: boolean;
  /** colorblind-friendly mode: pairs every color signal with a shape/icon (M3 accessibility) */
  colorblind: boolean;
  /**
   * Player-chosen UI text-size multiplier (M4 accessibility / WCAG 1.4.4). 1 = default. Applied to
   * every font-size via textScale()/scaledFont() in the render layer. Clamped 0.85..1.5 so a board
   * still fits at 360×640 at the largest step (UI reflows, never clips off-screen).
   */
  textScale: number;
}

/** Allowed UI text-scale steps (cycled by the Settings row). */
export const TEXT_SCALE_STEPS = [0.85, 1, 1.15, 1.3, 1.5] as const;
const TEXT_SCALE_MIN = TEXT_SCALE_STEPS[0];
const TEXT_SCALE_MAX = TEXT_SCALE_STEPS[TEXT_SCALE_STEPS.length - 1];

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
  } catch {
    return false;
  }
}

function defaults(): RenderSettings {
  return {
    volume: 0.7,
    muted: false,
    musicVolume: 1,
    sfxVolume: 1,
    reducedMotion: prefersReducedMotion(),
    haptics: true,
    colorblind: false,
    textScale: 1,
  };
}

let cache: RenderSettings | null = null;

function clamp01(n: unknown, fallback: number): number {
  return typeof n === 'number' && Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : fallback;
}

function clampScale(n: unknown, fallback: number): number {
  return typeof n === 'number' && Number.isFinite(n)
    ? Math.min(TEXT_SCALE_MAX, Math.max(TEXT_SCALE_MIN, n))
    : fallback;
}

/** Read the persisted settings (cached). Tolerant of malformed/absent storage. */
export function getSettings(): RenderSettings {
  if (cache) return cache;
  const d = defaults();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) {
      cache = d;
      return cache;
    }
    const obj = JSON.parse(raw) as Partial<RenderSettings>;
    cache = {
      volume: clamp01(obj.volume, d.volume),
      muted: typeof obj.muted === 'boolean' ? obj.muted : d.muted,
      musicVolume: clamp01(obj.musicVolume, d.musicVolume),
      sfxVolume: clamp01(obj.sfxVolume, d.sfxVolume),
      reducedMotion: typeof obj.reducedMotion === 'boolean' ? obj.reducedMotion : d.reducedMotion,
      haptics: typeof obj.haptics === 'boolean' ? obj.haptics : d.haptics,
      colorblind: typeof obj.colorblind === 'boolean' ? obj.colorblind : d.colorblind,
      textScale: clampScale(obj.textScale, d.textScale),
    };
  } catch {
    cache = d;
  }
  return cache;
}

/** Merge + persist a partial settings patch; returns the new full settings. */
export function setSettings(patch: Partial<RenderSettings>): RenderSettings {
  const next: RenderSettings = { ...getSettings(), ...patch };
  if (patch.volume !== undefined) next.volume = clamp01(patch.volume, getSettings().volume);
  if (patch.musicVolume !== undefined) next.musicVolume = clamp01(patch.musicVolume, getSettings().musicVolume);
  if (patch.sfxVolume !== undefined) next.sfxVolume = clamp01(patch.sfxVolume, getSettings().sfxVolume);
  if (patch.textScale !== undefined) next.textScale = clampScale(patch.textScale, getSettings().textScale);
  cache = next;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // private mode / blocked storage — settings live for the session only.
  }
  for (const fn of listeners) fn(next);
  return next;
}

type Listener = (s: RenderSettings) => void;
const listeners = new Set<Listener>();

/** Subscribe to settings changes (e.g. AudioBus re-applies volume live). Returns an unsubscribe. */
export function onSettingsChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Convenience: is screenshake / heavy-particle / hitstop juice allowed right now?
 *
 * Honours BOTH the explicit in-game "Reduced motion" toggle AND the OS `prefers-reduced-motion`
 * media query at call time — so if the player flips the OS setting mid-session (or arrived with it
 * on) motion is gated even before they open Settings. The explicit toggle can only ADD suppression,
 * never override an OS "reduce" request (WCAG 2.3.3 / SC 2.2.2): motion is allowed only when the
 * setting is off AND the OS is not asking to reduce.
 */
export function motionAllowed(): boolean {
  if (getSettings().reducedMotion) return false;
  if (prefersReducedMotion()) return false;
  return true;
}

/** Direct accessor for "is reduced-motion in effect" (setting OR OS query). */
export function reducedMotionActive(): boolean {
  return !motionAllowed();
}

/** Convenience: is colorblind-assist (shape/icon double-coding) on? */
export function colorblindActive(): boolean {
  return getSettings().colorblind;
}

/** Current UI text-scale multiplier (WCAG 1.4.4 resize support). */
export function textScale(): number {
  return getSettings().textScale;
}

/**
 * Scale a base font size (px) by the player's text-scale setting, with a floor so the smallest
 * labels never drop below a readable size at the smallest scale step. Use everywhere a px font is
 * computed so one setting resizes the whole UI consistently.
 */
export function scaledFont(basePx: number, minPx = 11): number {
  return Math.max(minPx, Math.round(basePx * textScale()));
}

// Live OS `prefers-reduced-motion` listener: when the player flips the OS-level setting mid-session,
// re-notify subscribers so scenes can kill any running looping tweens immediately (without a reload).
// We do NOT mutate the stored setting — motionAllowed() already reads the live query — we just nudge
// listeners to re-evaluate. Best-effort; tolerant of environments without matchMedia (headless/tests).
try {
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => {
      for (const fn of listeners) fn(getSettings());
    };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else if (typeof mq.addListener === 'function') mq.addListener(onChange); // older Safari
  }
} catch {
  // no matchMedia / blocked — the call-time motionAllowed() check still covers initial state.
}

/** Fire a haptic pulse if enabled + supported. Pattern in ms (number or array per Vibration API). */
export function haptic(pattern: number | number[]): void {
  const s = getSettings();
  if (!s.haptics) return;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch {
    // unsupported / blocked — silently ignore.
  }
}
